import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Icon from '../../../components/AppIcon';
import Footer from '../../../components/ui/Footer';

// =====================
// CONTENT EDITING SECTION
// =====================
const blogContent = {
    title: 'The Hard Truth About Finding a Job in India Today: Why "Normal" Isn\'t Enough Anymore',
    author: 'JobAspirant Team',
    date: '2026-01-02',
    readTime: '6 min read',
    category: 'Career Strategy',
    image: '/images/blog/indian-job-market-truth.png',

    // SEO Meta Tags
    metaDescription: 'Stuck in the "Applied and Ghosted" loop? Discover why traditional job searching is failing Indian candidates in 2026 and how to bridge your skill gaps.',
    keywords: 'job search india 2026, skill gap analysis, resume optimizer india, tech job market india, upskilling roadmap, resume ghosting solutions',

    // Blog Post Content Sections
    sections: [
        {
            type: 'paragraph',
            content: 'If you’ve been applying for jobs on major portals for months with no response, you aren\'t alone. Thousands of qualified Indian candidates are stuck in a loop of "Applied" and "Ghosted." The reality of the 2026 job market is that normal techniques are failing. Simply having a degree and a generic resume isn\'t cutting it anymore.',
        },
        {
            type: 'heading',
            content: '1. The Skill Gap vs. The Tech Disruption',
        },
        {
            type: 'paragraph',
            content: 'Companies have shifted. With rapid tech disruptions, "years of experience" are becoming less important than specific, provable skills. Major Indian employers are moving toward skill-based hiring. They don\'t just want to know where you worked; they want to know if you can handle the specific technical challenges of their role.',
        },
        {
            type: 'paragraph',
            content: 'Unfortunately, most candidates are still applying with resumes that reflect where they\'ve been, not what they can actually do for a new employer. This creates a massive "Skill Gap" that stops your application before a human recruiter even sees it.',
        },
        {
            type: 'heading',
            content: '2. The Failure of Major Job Portals',
        },
        {
            type: 'paragraph',
            content: 'We all use them—the big names like Naukri, LinkedIn, and Indeed. While they are great for finding listings, they are often a "Black Box" when it comes to helping you actually land the job:',
        },
        {
            type: 'list',
            items: [
                'No Pin-Point Analysis: Most portals treat resumes like static documents. They won\'t tell you exactly which 3 skills are missing for a specific Software Engineer or Analyst role.',
                'The "Black Box" Problem: They don’t tell you why you weren\'t a match. You’re left guessing if it was your formatting, a certification gap, or a keyword mismatch.',
                'Generic Advice: At best, they give you a "resume score," but they won\'t tell you the exact steps needed to target a specific MNC or startup.',
            ],
        },
        {
            type: 'heading',
            content: '3. You Need a Roadmap, Not Just a Score',
        },
        {
            type: 'paragraph',
            content: 'Knowing your resume is "70% good" doesn\'t help you get to 100%. Most platforms might offer basic tips, but they leave you hanging on the most important question: "What do I do next?" Without a structured upskilling roadmap, you end up wasting time on random courses that might not even be relevant to the job you actually want.',
        },
        {
            type: 'heading',
            content: 'How JobAspirant Changes the Game',
        },
        {
            type: 'paragraph',
            content: 'At JobAspirant, we realized that the "apply and pray" method is dead. We built this platform to solve the core challenges that Indian candidates face every day. We don’t just scan your resume; we uncover the truth about your application:',
        },
        {
            type: 'callout',
            content: 'Customized Gap Analysis: We take the specific job role you want and pinpoint the exact gaps in your profile. No more guessing.',
        },
        {
            type: 'callout',
            content: 'Curated Upskilling Roadmaps: We provide a structured path to close those gaps. You’ll know exactly what to learn to become the "ideal candidate" for that specific profile.',
        },
        {
            type: 'callout',
            content: 'The All-in-One Solution: We bring resume analysis, gap identification, and upskilling roadmaps into one easy-to-understand format.',
        },
        {
            type: 'heading',
            content: 'Stop Applying Blindly',
        },
        {
            type: 'paragraph',
            content: 'The goal isn\'t just to apply to more jobs; it\'s to get closer to the right job profile. It’s time to stop using the same old techniques and start using a system designed for the modern Indian workforce.',
        },
    ],
};

const BlogPost = () => {
    return (
        <>
            <Helmet>
                <title>{blogContent.title} | JobAspirant Blog</title>
                <meta name="description" content={blogContent.metaDescription} />
                <meta name="keywords" content={blogContent.keywords} />
                <link rel="canonical" href="https://jobaspirant.qubitlyventures.com/blog/the-hard-truth-about-finding-jobs-india" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="article" />
                <meta property="og:title" content={blogContent.title} />
                <meta property="og:description" content={blogContent.metaDescription} />
                <meta property="og:image" content={blogContent.image} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={blogContent.title} />
                <meta name="twitter:description" content={blogContent.metaDescription} />
                <meta name="twitter:image" content={blogContent.image} />
            </Helmet>

            <div className="min-h-screen bg-background flex flex-col">
                {/* Header */}
                <header className="border-b border-border bg-card">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex items-center justify-between">
                            <Link to="/" className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                                    <Icon name="Zap" size={24} color="white" />
                                </div>
                                <span className="text-2xl font-bold text-foreground">JobAspirant</span>
                            </Link>
                            <Link
                                to="/blog"
                                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                            >
                                ← Back to Blog
                            </Link>
                        </div>
                    </div>
                </header>

                {/* Article */}
                <article className="flex-1 py-12">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Article Header */}
                        <div className="mb-8">
                            <div className="flex items-center space-x-2 mb-4">
                                <span className="text-sm font-semibold text-primary bg-primary/10 px-3 py-1 rounded">
                                    {blogContent.category}
                                </span>
                                <span className="text-sm text-muted-foreground">{blogContent.readTime}</span>
                            </div>

                            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6 leading-tight">
                                {blogContent.title}
                            </h1>

                            <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-8">
                                <span>By {blogContent.author}</span>
                                <span>•</span>
                                <span>{new Date(blogContent.date).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</span>
                            </div>

                            {/* Featured Image placeholder logic */}
                            <div className="aspect-video bg-muted rounded-xl overflow-hidden mb-8 shadow-md">
                                <img
                                    src={blogContent.image}
                                    alt={blogContent.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.classList.add('flex', 'items-center', 'justify-center', 'bg-gradient-to-br', 'from-red-50', 'to-background');
                                        e.target.parentElement.innerHTML = '<div class="text-6xl">🚫📋</div>';
                                    }}
                                />
                            </div>
                        </div>

                        {/* Article Content */}
                        <div className="prose prose-lg max-w-none">
                            {blogContent.sections.map((section, index) => {
                                switch (section.type) {
                                    case 'heading':
                                        return (
                                            <h2 key={index} className="text-2xl sm:text-3xl font-bold text-foreground mt-10 mb-4 border-b pb-2">
                                                {section.content}
                                            </h2>
                                        );

                                    case 'paragraph':
                                        return (
                                            <p key={index} className="text-lg text-muted-foreground leading-relaxed mb-6">
                                                {section.content}
                                            </p>
                                        );

                                    case 'list':
                                        return (
                                            <div key={index} className="space-y-4 mb-8">
                                                {section.items.map((item, i) => {
                                                    const [boldPart, rest] = item.split(':');
                                                    return (
                                                        <div key={i} className="flex items-start space-x-3 bg-muted/20 p-4 rounded-lg">
                                                            <div className="mt-1">
                                                                <Icon name="AlertCircle" size={20} className="text-primary" />
                                                            </div>
                                                            <p className="text-lg text-muted-foreground">
                                                                <strong className="text-foreground">{boldPart}:</strong> {rest}
                                                            </p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );

                                    case 'callout':
                                        return (
                                            <div key={index} className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-lg mb-6 shadow-sm flex items-center space-x-4">
                                                <Icon name="CheckCircle" size={24} className="text-primary shrink-0" />
                                                <p className="text-lg text-foreground font-semibold">{section.content}</p>
                                            </div>
                                        );

                                    default:
                                        return null;
                                }
                            })}
                        </div>

                        {/* CTA Section */}
                        <div className="mt-16 p-10 bg-slate-900 rounded-3xl text-white text-center shadow-2xl relative overflow-hidden">
                            <div className="absolute top-0 right-0 p-4 opacity-10">
                                <Icon name="Zap" size={120} />
                            </div>
                            <h3 className="text-3xl font-bold mb-4 relative z-10">
                                Ready to see what’s actually missing?
                            </h3>
                            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto relative z-10">
                                Stop guessing why you aren't getting callbacks. Get your customized gap analysis and upskilling roadmap in seconds.
                            </p>
                            <Link
                                to="/login"
                                className="inline-flex items-center space-x-3 px-8 py-4 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-all shadow-lg hover:shadow-primary/20 relative z-10"
                            >
                                <span>Analyze Your Resume on JobAspirant</span>
                                <Icon name="ArrowRight" size={20} />
                            </Link>
                        </div>
                    </div>
                </article>

                <Footer />
            </div>
        </>
    );
};

export default BlogPost;